// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Generic renderer-driven dialog for displaying bill analysis results.
 *
 * Owns the Dialog chrome and footer; the body is dispatched via the
 * renderer-component registry. The bill renderers do their own payload
 * extraction off `slotData`.
 */
import { type FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, Loader2 } from 'lucide-react'

import type { WorkspaceRenderer } from '@/subsystems/agents'
import { useServices } from '@/app-shell/services'
import { getRendererComponent } from '@/app-shell/renderer-components'
import type { DatasetResponse } from '@/subsystems/documents/services'
import type { TokenUsage } from '../../hooks/useRendererColumns'

interface BillRendererDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  renderer: WorkspaceRenderer | null
  billNumber: string
  /** Pre-fetched dataset response — null if not yet loaded or not found. */
  response: DatasetResponse | null
  loading?: boolean
  tokenUsage?: TokenUsage | null
}

export const BillRendererDialog: FC<BillRendererDialogProps> = ({
  open,
  onOpenChange,
  renderer,
  billNumber,
  response,
  loading,
  tokenUsage,
}) => {
  const services = useServices()
  const apiClient = services.getClient()
  const title = renderer?.label ?? 'Analysis'
  const componentDef = getRendererComponent(renderer?.component)

  // Pre-migration tolerance: bind the response under the schema slot key when
  // possible, falling back to the renderer's first dataset slot.
  const slotKey = (() => {
    if (!renderer || !componentDef) return null
    const schemaDatasetSlot = componentDef.slots.find(s => s.type === 'dataset')
    if (schemaDatasetSlot && renderer.slots.some(s => s.key === schemaDatasetSlot.key && s.type === 'dataset')) {
      return schemaDatasetSlot.key
    }
    return renderer.slots.find(s => s.type === 'dataset')?.key ?? null
  })()
  const slotData: Record<string, DatasetResponse | null> = slotKey ? { [slotKey]: response } : {}

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading {title.toLowerCase()}...</span>
        </div>
      )
    }
    if (!response) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <AlertCircle className="h-5 w-5" />
          <span>No {title.toLowerCase()} data found for this bill.</span>
        </div>
      )
    }
    if (!renderer || !componentDef) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <AlertCircle className="h-5 w-5" />
          <span>Unsupported renderer component: <code>{renderer?.component ?? '(none)'}</code></span>
        </div>
      )
    }
    return (
      <componentDef.Component
        renderer={renderer}
        documentId=""
        orgId=""
        workspaceId=""
        agentId={renderer.agentId}
        apiClient={apiClient}
        slotData={slotData}
      />
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[80vh] overflow-y-auto" data-testid="lr-review-dialog">
        <DialogHeader>
          <DialogTitle>{title} - {billNumber}</DialogTitle>
          <DialogDescription>{title} results</DialogDescription>
        </DialogHeader>

        {renderBody()}
        {tokenUsage && <TokenUsageFooter tokenUsage={tokenUsage} />}
      </DialogContent>
    </Dialog>
  )
}

function formatTokenCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
}

function TokenUsageFooter({ tokenUsage }: { tokenUsage: TokenUsage }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 mt-4 text-xs text-muted-foreground">
      {tokenUsage.modelVariant && (
        <span className="font-medium">{tokenUsage.modelVariant}</span>
      )}
      <span>Prompt: {formatTokenCount(tokenUsage.promptTokens)}</span>
      <span>Completion: {formatTokenCount(tokenUsage.completionTokens)}</span>
      {tokenUsage.cacheReadTokens > 0 && (
        <span>Cache read: {formatTokenCount(tokenUsage.cacheReadTokens)}</span>
      )}
      {tokenUsage.cacheWriteTokens > 0 && (
        <span>Cache write: {formatTokenCount(tokenUsage.cacheWriteTokens)}</span>
      )}
    </div>
  )
}
